import React from 'react';

/*
 * PrivacyNotice — beta data-handling notice.
 *
 * EVERY CLAIM HERE IS GROUNDED IN CODE, and the grounding is cited inline so the
 * next person can re-check it rather than trust it. Written 2026-08-26 because
 * operators are being asked to upload their clients' estimates and there was no
 * statement of what happens to them.
 *
 * THIS IS NOT A LAWYER-REVIEWED PRIVACY POLICY and says so. It is an accurate
 * description of current behaviour, which is strictly better than silence and is
 * what a beta user is owed. It must be replaced by a reviewed document before
 * anyone signs a commercial agreement.
 *
 * RULE FOR EDITING: if you change what the platform does with a file, change this
 * page in the same commit. A stale privacy notice is worse than none — it is a
 * written statement that has become untrue.
 */

const SECTIONS = [
    {
        h: 'What Audee receives',
        p: [
            'The estimate or invoice files you upload, and the email address you signed in with. Nothing else is collected about you — there is no tracking pixel, no advertising identifier, and no third-party analytics on this application.',
        ],
    },
    {
        h: 'Where your files are processed',
        p: [
            // document-ingestion.js: native pdf-parse first; ocr-adapter.js only on
            // the image-only fallback path.
            'Estimates exported directly from Xactimate, Symbility or Servpro carry a text layer, and those are read entirely on Audee’s own servers. The file is not sent to any third party.',
            'Scanned or photographed documents have no text layer. Those pages — and only those — are sent to Anthropic’s Claude vision API to convert the images into text so the estimate can be read. Anthropic processes them to return that text and Audee does not permit their use for model training. If you would rather no document leave Audee’s servers at all, upload a PDF exported from your estimating software rather than a scan or a photograph.',
        ],
    },
    {
        h: 'What Audee reads from an estimate',
        p: [
            // Deliberately specific rather than a blanket "we only take line items".
            // That claim would be false — and it would also mean giving up the
            // related-party check below, which is a genuinely good finding.
            'Audee reads an estimate the way an auditor would: the line items, quantities, rates, totals and schedules, plus the cover-page fields the audit actually depends on. That means the loss date and price list, whether a claim number is documented, the property’s state, and the estimator and insured names — which are compared against each other to flag an estimate written for a related party.',
            // policyNumber + estimatorCompany capture removed 2026-08-26; existing
            // stored values purged in the same change.
            'Audee does not retain policy numbers. Nothing on the cover page is kept unless a rule reads it — a field no finding depends on is collection without a purpose, and this is a customer’s insurance paperwork.',
            'Audee builds no profile of a homeowner or a policyholder, never uses any of this to identify or contact anyone, and never sells or shares it. What Audee holds is the estimate, not a customer record.',
            // Said plainly because a policy sentence cannot fix it and pretending
            // otherwise would be the dishonest part.
            'One thing worth knowing: the file name you upload is stored as you sent it. If your naming convention includes a client name or a property address, that travels with the file. Rename before uploading if that matters to you.',
        ],
    },
    {
        h: 'Audee does not learn from your estimates unless you say so',
        p: [
            // accounts.corpus_opt_in — migration 0002, "not null default false".
            'Contributing your documents to the corpus Audee learns from is off by default and stays off until an account owner turns it on. This is enforced in the database, not by policy: the opt-in column defaults to false and every change to it is recorded with a timestamp.',
            'If you never opt in, your estimates are never used to develop Audee’s rules or its line-item vocabulary.',
        ],
    },
    {
        h: 'What is kept, and for how long',
        p: [
            // audit_history.parsed_data_json / findings_json.
            'When an audit succeeds, Audee keeps the extracted contents of the document and the findings produced from it, so the report stays available in your audit history. It is visible to you and to members of your account.',
            // audit-failure-log.js: shouldStoreFailedDocument gated on manualReview;
            // RETENTION_DAYS = 30; STORAGE_CONSENT_BASIS = 'manual_review_support'.
            'When an audit fails in a way where a person could help, Audee keeps a copy of the file for up to 30 days so that person can look at it, then deletes it. The file sits in a private store that is never publicly addressable, and every stored file records why it was kept and when it expires.',
            // unsupported_symbility, unsupported_tm_format, unsupported_estimate_type: manualReview false (2026-09-09).
            'When Audee recognises a format it has deliberately chosen not to audit yet — a Symbility estimate, a T&M Pro invoice, or an Excel-format T&M bill today — the file is not retained at all. There is nothing to review that Audee does not already have.',
        ],
    },
    {
        h: 'Who can see it',
        p: [
            'You and the members of your account. Audee staff can retrieve a stored failed upload only through an authenticated administrator session using a short-lived link, and only for the specific failure you were shown.',
            'Audee does not sell your data, does not share it with advertisers, and does not show advertising.',
        ],
    },
    {
        h: 'Service providers',
        p: [
            'Audee runs on Supabase (database and file storage), Railway (application servers) and Vercel (web front end). Anthropic is used for reading scanned documents, as described above. Resend sends transactional email and Stripe handles payment details, which Audee never sees or stores.',
        ],
    },
    {
        h: 'Deleting your data',
        p: [
            'Email logan@audee.io and Audee will delete your account, your uploaded files and your audit history. During the beta this is handled by a person rather than a button, so allow a few working days and ask for confirmation once it is done.',
        ],
    },
];

function PrivacyNotice({ onClose }) {
    return (
        <div className="privacy-overlay" role="dialog" aria-modal="true" aria-label="Privacy and data handling">
            <div className="privacy-panel">
                <div className="privacy-head">
                    <h2 className="privacy-title">Privacy &amp; data handling</h2>
                    <button className="privacy-close" onClick={onClose} aria-label="Close">×</button>
                </div>

                <p className="privacy-updated">Beta notice · last updated 26 August 2026</p>

                <p className="privacy-lede">
                    Audee is in beta and this is a plain description of what currently
                    happens to the documents you upload. It has not been reviewed by a
                    lawyer and is not a contract. If your organisation needs a formal
                    data-processing agreement before you upload client work, ask for one
                    first — that is a reasonable thing to want, and nothing here is a
                    substitute for it.
                </p>

                {SECTIONS.map(s => (
                    <section className="privacy-section" key={s.h}>
                        <h3 className="privacy-h">{s.h}</h3>
                        {s.p.map((t, i) => <p className="privacy-p" key={i}>{t}</p>)}
                    </section>
                ))}

                <p className="privacy-foot">
                    Questions, or anything here that does not match what you have seen —
                    <a href="mailto:logan@audee.io"> logan@audee.io</a>.
                </p>
            </div>
        </div>
    );
}

export default PrivacyNotice;
